/**
 * Drizzle ORM schema for Cloudflare D1.
 *
 * Only the tables needed for the initial Plumbing + Obligations route are
 * defined here. Other entities (tasks, documents, photos, people, etc.)
 * are deliberately deferred until their routes are built — see PROJECT.md
 * §Build sequence and DATA_MODEL.md.
 */

import { sql } from 'drizzle-orm';
import { integer, primaryKey, sqliteTable, text, uniqueIndex } from 'drizzle-orm/sqlite-core';

export const users = sqliteTable('users', {
  id: text('id').primaryKey(),
  email: text('email').notNull().unique(),
  displayName: text('display_name').notNull(),
  role: text('role').notNull(),
  archiveVisible: integer('archive_visible', { mode: 'boolean' }).notNull().default(false),
  hubVisible: integer('hub_visible', { mode: 'boolean' }).notNull().default(false),
  // IANA timezone (`Europe/Zurich`, `America/New_York`, …) gathered
  // from the browser when push notifications are enabled. Used by
  // the cron worker to translate an obligation's local `HH:MM`
  // into the UTC instant the notification should fire.
  timezone: text('timezone'),
  // Per-user home-screen layout: 'summary' (obligations panel +
  // compact tiles) or 'tiles' (large section tiles, no panel).
  // Synced across devices, so it lives here rather than in
  // localStorage. See migration 0011.
  homeLayout: text('home_layout').notNull().default('summary'),
  // Per-user AI model for document extraction (Documents Phase 3).
  // Nullable: null = the app default (Sonnet 5). See migration 0015.
  documentAiModel: text('document_ai_model'),
  // "This is me": the person record this login corresponds to. Powers
  // You badges and the "Me" picker shortcut. See migration 0018.
  personId: text('person_id'),
  // Preferred display currency for the Budget page (ISO 4217 code).
  // Nullable: null = CHF, the household default. See migration 0025.
  currency: text('currency'),
  createdAt: text('created_at').notNull(),
  lastSeenAt: text('last_seen_at'),
});

export const lifeAreas = sqliteTable('life_areas', {
  id: text('id').primaryKey(),
  name: text('name').notNull(),
  slug: text('slug').notNull().unique(),
  icon: text('icon'),
  sortOrder: integer('sort_order').notNull().default(0),
  visibility: text('visibility').notNull().default('shared'),
  createdAt: text('created_at').notNull(),
  updatedAt: text('updated_at').notNull(),
});

export const obligations = sqliteTable('obligations', {
  id: text('id').primaryKey(),
  title: text('title').notNull(),
  description: text('description'),
  lifeAreaId: text('life_area_id')
    .notNull()
    .references(() => lifeAreas.id),
  dueDate: text('due_date').notNull(),
  // Optional time-of-day pinned to the dueDate, stored as `HH:MM`
  // (24-hour, zero-padded). Null when the obligation is an all-day
  // item — most are. The recurrence rule continues to schedule by
  // date only; the time is a constant applied to whatever the next
  // occurrence is, not part of the cadence.
  dueTime: text('due_time'),
  nextDueDate: text('next_due_date'),
  recurrenceRule: text('recurrence_rule'),
  priority: text('priority').notNull().default('medium'),
  status: text('status').notNull().default('active'),
  reminderDaysBefore: text('reminder_days_before'),
  notes: text('notes'),
  visibility: text('visibility').notNull().default('private'),
  // `kind` distinguishes a generic dated task from an occasion (birthday,
  // anniversary). Occasions carry person links and an anchor year so the
  // UI can render "turns 40" rather than just a checkbox row.
  kind: text('kind').notNull().default('task'),
  occasionType: text('occasion_type'),
  // Free-text 'what is this anniversary' (Wedding, Partnership, Met,
  // Graduated…). Shown on the list card as 'Wedding · 25 years'
  // when set, falls back to 'Anniversary · 25 years' when null.
  occasionLabel: text('occasion_label'),
  anchorYear: integer('anchor_year'),
  // Occasion → task suggestion: the occurrence date the "add a gift
  // reminder" prompt was dismissed for. Stale (and so re-armed) once
  // the occurrence rolls over to the next year. See migration 0022.
  suggestionDismissedFor: text('suggestion_dismissed_for'),
  createdBy: text('created_by')
    .notNull()
    .references(() => users.id),
  createdAt: text('created_at').notNull(),
  updatedAt: text('updated_at').notNull(),
  completedAt: text('completed_at'),
  // The trip this task belongs to, if any (pre-trip errands live on
  // the trip page as well as in Obligations). See migration 0026.
  tripId: text('trip_id'),
  // ISO timestamp of the last push notification fired for this
  // obligation, or null if none. Compared against the obligation's
  // current target moment to dedupe minute-cron ticks within the
  // same firing window. See migration 0008 for the firing rule.
  lastNotifiedAt: text('last_notified_at'),
  deletedAt: text('deleted_at'),
});

export const obligationDocuments = sqliteTable(
  'obligation_documents',
  {
    obligationId: text('obligation_id')
      .notNull()
      .references(() => obligations.id),
    // documents table not yet created — referenced by id only for now.
    documentId: text('document_id').notNull(),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.obligationId, table.documentId] }),
  }),
);

// Metadata index for administrative documents (insurance, permits,
// warranties, medical records). The file lives in Dropbox; this row
// carries the searchable metadata and the path. See DATA_MODEL.md
// §documents — this is a pragmatic subset (vault-only fields land with
// the Vault/upload work). Owner-private by default like obligations.
export const documents = sqliteTable('documents', {
  id: text('id').primaryKey(),
  title: text('title').notNull(),
  lifeAreaId: text('life_area_id')
    .notNull()
    .references(() => lifeAreas.id),
  // 'routine' | 'vital' | 'retired'. Present from the first migration
  // so the future Vault (vital records) is a behaviour change, not a
  // schema change. ('retired' = my own old-but-keep records; "archive"
  // is reserved for Folio's family archive — see PROJECT.md.)
  documentCategory: text('document_category').notNull().default('routine'),
  // Finer free-text type within the category ('insurance', 'passport',
  // 'warranty', …). Free text, not an enum, so the vocabulary grows
  // without a migration.
  category: text('category'),
  // Optional sub-area within the life area ('Dentist' under Health).
  // Free text with per-area reuse in the UI; adds a folder segment to
  // the Dropbox mirror path. See migration 0020.
  subArea: text('sub_area'),
  // JSON-encoded array of `people` ids — who the document is about
  // (self, spouse, children, others). Lets the catalogue group/filter
  // by person. A JSON column (not a join table) is enough at personal
  // scale; upgrade to document_people if querying ever needs it.
  pertainsTo: text('pertains_to'),
  issuer: text('issuer'),
  issuedDate: text('issued_date'),
  // Indexed for the "what's expiring" query.
  expiryDate: text('expiry_date'),
  // The expiry date attention was dismissed for ("I've handled it") —
  // either quietly or by spinning up a renewal obligation. Compared to
  // expiry_date; a changed expiry makes the dismissal stale so the
  // attention surfaces resume on their own. See migration 0021.
  expiryDismissedFor: text('expiry_dismissed_for'),
  // Vault annual review: when the user last confirmed this vital
  // record is still current. Null = never; created_at is the fallback
  // anchor for the "review due" calculation. See migration 0023.
  vaultReviewedAt: text('vault_reviewed_at'),
  // Per-record review cadence in calendar months (null = 12). A
  // lapsing passport can ask for 6, mortgage papers 24. Migration 0024.
  vaultReviewMonths: integer('vault_review_months'),
  // Dedupe stamp for the vault-review push — fired once per cycle,
  // compared against the review's target instant. Migration 0024.
  vaultReviewNotifiedAt: text('vault_review_notified_at'),
  // Where the file lives. Nullable in this metadata-first cut — a
  // record can exist before a path is wired up. (Free-text reference;
  // the Dropbox mirror with derived paths lands in a later phase.)
  dropboxPath: text('dropbox_path'),
  // The Vault's second Dropbox copy (/Vault/<Kind>/…, Retired records
  // under /Vault/Retired/). A *copy*, not a move — the Documents-tree
  // mirror above stays put so the file is findable both ways. Server-
  // managed alongside dropbox_path. See migration 0023.
  dropboxVaultPath: text('dropbox_vault_path'),
  // The uploaded file in R2 (the `BLOBS` bucket). `fileKey` is the
  // object key (internal — never exposed on the wire); the rest is
  // metadata for display and integrity. NULL = no file attached yet.
  // canonical_store is implicitly 'r2' when fileKey is set; the Dropbox
  // mirror (later phase) will add its own path/sync columns.
  fileKey: text('file_key'),
  fileName: text('file_name'),
  mimeType: text('mime_type'),
  fileSize: integer('file_size'),
  fileHash: text('file_hash'),
  // JSON-encoded array of strings.
  tags: text('tags'),
  notes: text('notes'),
  // Key-document flag: one of the handful that matter for a person
  // (passport, vaccination record). Starred documents surface on the
  // person card of everyone they pertain to. See migration 0016.
  starred: integer('starred', { mode: 'boolean' }).notNull().default(false),
  // The trip this document belongs to, if any (bookings, tickets).
  // See migration 0026.
  tripId: text('trip_id'),
  visibility: text('visibility').notNull().default('private'),
  createdBy: text('created_by')
    .notNull()
    .references(() => users.id),
  createdAt: text('created_at').notNull(),
  updatedAt: text('updated_at').notNull(),
  deletedAt: text('deleted_at'),
});

// Shared cross-cutting entity (see PROJECT.md §carve-out). Almanac
// owns it for now; Folio will either duplicate or read it via a
// service when the archive packages are carved out later. Kept
// minimal here on purpose — archive-specific fields land when Folio
// needs them.
export const people = sqliteTable('people', {
  id: text('id').primaryKey(),
  displayName: text('display_name').notNull(),
  relationship: text('relationship'),
  notes: text('notes'),
  // The person's photo in R2 (the `BLOBS` bucket) — object key is
  // internal, served via GET /api/shared/people/:id/avatar. NULL = no
  // photo; the UI falls back to initials. See migration 0017.
  avatarKey: text('avatar_key'),
  // Explicit household membership: groups first in the People list and
  // leads every person picker. See migration 0019.
  household: integer('household', { mode: 'boolean' }).notNull().default(false),
  createdBy: text('created_by')
    .notNull()
    .references(() => users.id),
  createdAt: text('created_at').notNull(),
  updatedAt: text('updated_at').notNull(),
  deletedAt: text('deleted_at'),
});

// Many-to-many: a person can be related to several others (Oscar is
// the son of both parents). Direction is meaningful — `person_id` is
// the subject ("the son"), `related_person_id` is the object ("of
// Mihaela"). The `relationship` text on `people` is the subject's
// role used across all their links (free-text: "son", "spouse",
// "colleague"); per-link role can be layered on later if a single
// person needs different roles per relation.
export const personRelationships = sqliteTable(
  'person_relationships',
  {
    personId: text('person_id')
      .notNull()
      .references(() => people.id),
    relatedPersonId: text('related_person_id')
      .notNull()
      .references(() => people.id),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.personId, table.relatedPersonId] }),
  }),
);

export const obligationPeople = sqliteTable(
  'obligation_people',
  {
    obligationId: text('obligation_id')
      .notNull()
      .references(() => obligations.id),
    personId: text('person_id')
      .notNull()
      .references(() => people.id),
    // Optional — useful when a single obligation has multiple subjects
    // (joint anniversary, parent-child birthday).
    role: text('role'),
  },
  (table) => ({
    pk: primaryKey({ columns: [table.obligationId, table.personId] }),
  }),
);

export const auditLog = sqliteTable('audit_log', {
  id: text('id').primaryKey(),
  entityType: text('entity_type').notNull(),
  entityId: text('entity_id').notNull(),
  action: text('action').notNull(),
  diff: text('diff'),
  userId: text('user_id')
    .notNull()
    .references(() => users.id),
  occurredAt: text('occurred_at')
    .notNull()
    .default(sql`(current_timestamp)`),
});

// One row per device that has opted in to web-push reminders. The
// browser push-service `endpoint` URL is unique per device, but
// iOS Safari regenerates it on every SW replacement — so the
// natural upsert key is `device_id` (a stable UUID generated by
// the client and persisted in localStorage). `p256dh` + `auth`
// are the keys the cron worker needs to encrypt push payloads to
// this device. `last_seen_at` is refreshed on each re-subscribe
// so stale rows can be aged out later.
// Budget months: one row per user per month, the whole plan stored as
// a JSON snapshot (BudgetSnapshot in budget.ts). Strictly per-user —
// no visibility column; every query filters on user_id. Months are
// independent copies, so trends and carry-forward need no history
// machinery. See migration 0025.
export const budgetMonths = sqliteTable(
  'budget_months',
  {
    id: text('id').primaryKey(),
    userId: text('user_id')
      .notNull()
      .references(() => users.id),
    month: text('month').notNull(), // 'YYYY-MM'
    data: text('data').notNull(), // JSON BudgetSnapshot
    createdAt: text('created_at').notNull(),
    updatedAt: text('updated_at').notNull(),
  },
  (table) => ({
    userMonth: uniqueIndex('budget_months_user_month').on(table.userId, table.month),
  }),
);

// Travel: trips as containers of tasks, documents, dates, and a
// packing checklist (JSON snapshot, like budget months). Obligations
// and documents point in via their trip_id. See migration 0026.
export const trips = sqliteTable('trips', {
  id: text('id').primaryKey(),
  title: text('title').notNull(),
  destination: text('destination'),
  startDate: text('start_date').notNull(),
  endDate: text('end_date'),
  notes: text('notes'),
  packing: text('packing'), // JSON PackingItem[]
  visibility: text('visibility').notNull().default('private'),
  createdBy: text('created_by')
    .notNull()
    .references(() => users.id),
  createdAt: text('created_at').notNull(),
  updatedAt: text('updated_at').notNull(),
  deletedAt: text('deleted_at'),
});

export const pushSubscriptions = sqliteTable('push_subscriptions', {
  id: text('id').primaryKey(),
  userId: text('user_id')
    .notNull()
    .references(() => users.id),
  deviceId: text('device_id'),
  endpoint: text('endpoint').notNull().unique(),
  p256dh: text('p256dh').notNull(),
  auth: text('auth').notNull(),
  userAgent: text('user_agent'),
  createdAt: text('created_at').notNull(),
  lastSeenAt: text('last_seen_at').notNull(),
  // Last time the SW POSTed `/subscriptions/ack` after handling a
  // push. Null until the first ack arrives; once set, indicates
  // pushes are actually reaching the device.
  lastAckedAt: text('last_acked_at'),
  // Pushes the cron has fired to this subscription that the device
  // hasn't acknowledged. Incremented on each successful APNs send,
  // reset to 0 on every ack. The Settings sheet uses this to spot
  // a silently-broken subscription (e.g. iOS Chrome-installed PWA)
  // and nudge the user to reinstall via Safari.
  pushesSinceAck: integer('pushes_since_ack').notNull().default(0),
});

export type User = typeof users.$inferSelect;
export type NewUser = typeof users.$inferInsert;

// Suffix `Row` to dodge the DOM lib's global `PushSubscription`
// type — the client subscription code wants the browser's version
// in scope, not this drizzle-inferred row shape.
export type PushSubscriptionRow = typeof pushSubscriptions.$inferSelect;
export type NewPushSubscriptionRow = typeof pushSubscriptions.$inferInsert;

export type LifeArea = typeof lifeAreas.$inferSelect;
export type NewLifeArea = typeof lifeAreas.$inferInsert;

export type Obligation = typeof obligations.$inferSelect;
export type NewObligation = typeof obligations.$inferInsert;

export type DocumentRow = typeof documents.$inferSelect;
export type NewDocumentRow = typeof documents.$inferInsert;

export type Person = typeof people.$inferSelect;
export type NewPerson = typeof people.$inferInsert;

export type ObligationPerson = typeof obligationPeople.$inferSelect;
export type NewObligationPerson = typeof obligationPeople.$inferInsert;

export type PersonRelationship = typeof personRelationships.$inferSelect;
export type NewPersonRelationship = typeof personRelationships.$inferInsert;

export type BudgetMonthRow = typeof budgetMonths.$inferSelect;
export type NewBudgetMonthRow = typeof budgetMonths.$inferInsert;

export type TripRow = typeof trips.$inferSelect;
export type NewTripRow = typeof trips.$inferInsert;

export type AuditLogEntry = typeof auditLog.$inferSelect;
export type NewAuditLogEntry = typeof auditLog.$inferInsert;
